/**
 * ============================================================================
 * AI HELPERS - AI Color Magic & Wedding Application Functions
 * ============================================================================
 * 
 * PURPOSE:
 * Provides AI-powered color exploration and wedding application suggestions.
 * Handles the "AI Color Magic" feature that generates smart recommendations
 * for how to use specific colors in wedding planning.
 * 
 * KEY FUNCTIONS:
 * 
 * 1. triggerAIMagic()
 *    - Triggers AI Color Magic workflow
 *    - Shows loading animation
 *    - Calls backend for AI-generated color applications
 *    - Handles model selection (compare mode uses source model)
 * 
 * 2. getSmartWeddingApplications()
 *    - Fetches AI-generated wedding applications for a color
 *    - POST /api/dream/smart-suggestions
 *    - Returns: bridesmaid dresses, flowers, decor, etc.
 * 
 * 3. getDisplayApplications()
 *    - Formats applications for UI display
 *    - Handles loading state
 *    - Handles error state
 *    - Returns formatted application list
 * 
 * USAGE:
 * - Used by DreamWeddingGenerator and MoodboardMatcher
 * - Triggered when user clicks "AI Color Magic" button
 * - Displays modal with AI-generated suggestions
 * 
 * BACKEND INTEGRATION:
 * - POST /api/dream/smart-suggestions
 * - Sends: color hex, color name, model
 * - Receives: AI-generated wedding applications
 * 
 * ============================================================================
 */

// AI Integration Helper Functions
// Extracted from Dream Wedding Generator and Moodboard Matcher

// Note: apiService import will be handled by the consuming component

/**
 * Trigger AI Magic workflow - Shows loading and fetches AI suggestions
 * 
 * @param {Object} colorModal - Current color modal state
 * @param {Function} setColorModal - State setter for color modal
 * @param {String} selectedModel - Active AI model (gemini/claude/openai)
 * @param {Object} apiService - API service for backend calls
 */
export const triggerAIMagic = async (colorModal, setColorModal, selectedModel, apiService) => {
  // Show loading animation
  setColorModal(prev => ({
    ...prev,
    applications: ['LOADING_AI_MAGIC']  // Special flag triggers loading animation
  }));
  
  // ========================================================================
  // STEP 2: DETERMINE WHICH AI MODEL TO USE
  // ========================================================================
  // In compare mode, use the specific model that generated the theme
  // In single mode, use the selected model
  let modelToUse;
  if (colorModal.sourceModel) {
    // Compare Mode: use the exact model that generated this theme
    // Ensures consistency (e.g., if Claude generated theme, use Claude for color magic)
    modelToUse = colorModal.sourceModel;
  } else {
    // Single Mode: use selected model, fallback to 'gemini' if 'compare'
    modelToUse = selectedModel === 'compare' ? 'gemini' : selectedModel;
  }
  
  // ========================================================================
  // STEP 3: FETCH AI-GENERATED COLOR APPLICATIONS
  // ========================================================================
  // Call backend API to get wedding applications for this color
  // Returns categorized suggestions: { decor: [...], flowers: [...], attire: [...] }
  const smartApplications = await getSmartWeddingApplications(
    colorModal.color,       // Hex code (e.g., '#FFB6C1')
    colorModal.colorName,   // Human-readable name (e.g., 'Blush Pink')
    modelToUse,             // AI model to use
    apiService              // API service instance
  );
  
  // ========================================================================
  // STEP 4: UPDATE MODAL WITH RESULTS
  // ========================================================================
  // Check if API returned an error
  if (Array.isArray(smartApplications) && smartApplications.includes('API_ERROR')) {
    setColorModal(prev => ({
      ...prev,
      applications: ['API_ERROR_MESSAGE']
    }));
  } else {
    setColorModal(prev => ({
      ...prev,
      applications: smartApplications
    }));
  }
};

// ============================================================================
// GET SMART WEDDING APPLICATIONS
// ============================================================================

/**
 * Fetch AI-powered wedding applications for a specific color
 * 
 * WHAT IT DOES:
 * 1. Sends color data to backend smart suggestions API
 * 2. Backend uses AI (Gemini/Claude/OpenAI) to generate applications
 * 3. Parses response into categorized suggestions
 * 4. Falls back to rule-based suggestions if AI fails
 * 
 * BACKEND API:
 * - POST /api/dream/smart-suggestions
 * - Input: { color, colorName, model }
 * - Output: { decor: [...], flowers: [...], attire: [...], ... }
 * 
 * CATEGORIES:
 * - decor: Table settings, centerpieces, lighting
 * - flowers: Bouquets, arrangements, boutonnieres
 * - attire: Bridesmaid dresses, groomsmen suits, accessories
 * - stationery: Invitations, programs, place cards
 * - cake: Cake design, frosting, decorations
 * - lighting: Uplighting, string lights, candles
 * 
 * FALLBACK:
 * - If AI fails, uses rule-based getWeddingApplications()
 * - Ensures feature always works
 * 
 * @param {String} hex - Hex color code
 * @param {String} colorName - Human-readable color name
 * @param {String} selectedModel - AI model: 'gemini', 'claude', 'openai'
 * @param {Object} apiService - API service instance
 * @returns {Object|Array} Categorized suggestions or error indicator
 */
export const getSmartWeddingApplications = async (hex, colorName, selectedModel, apiService) => {
  try {
    // ========================================================================
    // STEP 1: BUILD REQUEST DATA
    // ========================================================================
    const requestData = {
      color: hex,                           // Hex code (e.g., '#FFB6C1')
      colorName: colorName,                 // Human-readable name (e.g., 'Blush Pink')
      model: selectedModel || 'auto',       // AI model or 'auto' for backend selection
      timestamp: Date.now()                 // Timestamp for cache busting
    };
    
    console.log('🚀 Making POST request to smart suggestions API:', requestData);
    
    // ========================================================================
    // STEP 2: CALL BACKEND API
    // ========================================================================
    // POST /api/dream/smart-suggestions
    const response = await apiService.dream.generateSmartSuggestions(requestData);

    console.log('📡 Response status:', response.status);
    console.log('📡 Response data:', response.data);

    // ========================================================================
    // STEP 3: PARSE RESPONSE
    // ========================================================================
    if (response.status === 200 && response.data) {
      // Try to parse JSON response for categorized suggestions
      try {
        let suggestions = response.data.suggestions;
        
        // Handle string response (AI may return JSON as string)
        if (typeof suggestions === 'string') {
          // Extract JSON from string
          const jsonMatch = suggestions.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            suggestions = JSON.parse(jsonMatch[0]);
          }
        }
        
        // Validate suggestions format
        if (typeof suggestions === 'object' && suggestions !== null) {
          console.log('✅ Successfully parsed categorized suggestions:', suggestions);
          return suggestions;  // Return categorized object
        } else {
          // Invalid format - use fallback
          console.log('⚠️ Suggestions not in expected format, using fallback');
          return getWeddingApplications(hex);
        }
      } catch (parseError) {
        // JSON parsing failed - use fallback
        console.log('⚠️ JSON parsing failed, using fallback:', parseError);
        return getWeddingApplications(hex);
      }
    } else {
      console.log('⚠️ API response not successful, using fallback');
      return getWeddingApplications(hex);
    }
  } catch (error) {
    console.error('❌ Smart suggestions API failed:', error);
    return getWeddingApplications(hex);
  }
};

// ============================================================================
// GET DISPLAY APPLICATIONS
// ============================================================================

/**
 * Format applications for UI display based on modal state
 * 
 * WHAT IT DOES:
 * 1. Checks for special states (loading, error)
 * 2. Extracts applications for selected category
 * 3. Handles different data formats (object vs array)
 * 4. Returns array ready for display
 * 
 * SPECIAL STATES:
 * - ['LOADING_AI_MAGIC']: Show loading animation
 * - ['API_ERROR_MESSAGE']: Show error message
 * - ['SHOW_AI_BUTTON']: Show "AI Color Magic" button
 * 
 * DATA FORMATS:
 * - Object (AI response): { decor: [...], flowers: [...], attire: [...] }
 * - Array (fallback): ['Application 1', 'Application 2', ...]
 * 
 * CATEGORY SELECTION:
 * - Uses colorModal.selectedCategory to extract relevant applications
 * - Default: 'decor'
 * - Options: decor, flowers, attire, stationery, cake, lighting
 * 
 * @param {Object} colorModal - Color modal state
 * @returns {Array} Applications to display in modal
 */
export const getDisplayApplications = (colorModal) => {
  // ========================================================================
  // VALIDATION: CHECK IF APPLICATIONS EXIST
  // ========================================================================
  if (!colorModal.applications) return [];
  
  // ========================================================================
  // SPECIAL STATE: LOADING
  // ========================================================================
  // Check if array first to avoid errors
  if (Array.isArray(colorModal.applications) && colorModal.applications.includes('LOADING_AI_MAGIC')) {
    return ['LOADING_AI_MAGIC'];  // Triggers loading animation in modal
  }
  
  // ========================================================================
  // SPECIAL STATE: ERROR
  // ========================================================================
  // Check if array first to avoid errors
  if (Array.isArray(colorModal.applications) && colorModal.applications.includes('API_ERROR_MESSAGE')) {
    return ['API_ERROR_MESSAGE'];  // Triggers error message in modal
  }
  
  // ========================================================================
  // FORMAT: CATEGORIZED OBJECT (AI RESPONSE)
  // ========================================================================
  // Handle categorized applications from AI
  // Structure: { decor: [...], flowers: [...], attire: [...] }
  if (typeof colorModal.applications === 'object' && !Array.isArray(colorModal.applications)) {
    const category = colorModal.selectedCategory || 'decor';  // Default to 'decor'
    return colorModal.applications[category] || [];  // Return category array or empty
  }
  
  // ========================================================================
  // FORMAT: SIMPLE ARRAY (FALLBACK)
  // ========================================================================
  // Handle simple array from fallback function
  if (Array.isArray(colorModal.applications)) {
    return colorModal.applications;
  }
  
  // ========================================================================
  // DEFAULT: EMPTY ARRAY
  // ========================================================================
  return [];
};

// ============================================================================
// FALLBACK WEDDING APPLICATIONS
// ============================================================================

/**
 * Fallback function for wedding applications (when AI fails)
 * Returns basic color-based suggestions
 * Ensures feature always works even without AI
 * 
 * @param {String} hex - Hex color code
 * @returns {Array} Basic wedding application suggestions
 */
export const getWeddingApplications = (hex) => {
  // Basic color-based wedding suggestions as fallback
  const applications = [
    `Table linens and napkins in ${hex}`,
    `Floral arrangements featuring this color`,
    `Bridesmaid dresses or accessories`,
    `Wedding stationery and invitations`,
    `Ceremony decorations and draping`
  ];
  
  return {
    decor: applications,
    flowers: [`Bouquet featuring ${hex} blooms`, `Centerpieces with this color palette`],
    attire: [`Bridesmaid dresses in ${hex}`, `Groomsmen accessories`],
    lighting: [`Ambient lighting in ${hex} tones`, `Uplighting effects`],
    stationery: [`Invitations with ${hex} accents`, `Menu cards and programs`],
    food: [`Cake decorations in ${hex}`, `Signature cocktails with this theme`]
  };
};
